import useUserQueryStore from "../context/UserStore";
import { useQuery } from "@tanstack/react-query";
import MultiTenantClusterGetDto, { UserApis } from "../dtos/multiTenantClusterDto";
import APIClient from "../services/apiClient";
import ms from "ms";

type AnyRecord = Record<string, unknown>;

const asRecord = (value: unknown): AnyRecord | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? (value as AnyRecord) : undefined;

const pickString = (source: AnyRecord | undefined, ...keys: string[]): string | undefined => {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string") return value;
  }
  return undefined;
};

const pickNumber = (source: AnyRecord | undefined, ...keys: string[]): number | undefined => {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && !Number.isNaN(value)) return value;
  }
  return undefined;
};

const pickStringArray = (source: AnyRecord | undefined, ...keys: string[]): string[] => {
  if (!source) return [];
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string");
    }
  }
  return [];
};

const normalizeUserApis = (raw: unknown): UserApis => {
  const record = asRecord(raw) ?? {};
  return {
    iManage: pickString(record, "iManage", "IManage") ?? "",
    Settings: pickString(record, "Settings", "settings") ?? "",
    Lookups: pickString(record, "Lookups", "lookups") ?? "",
  };
};

const normalizeCluster = (doc: unknown): MultiTenantClusterGetDto => {
  const source = asRecord(doc) ?? {};

  return {
    id: pickString(source, "id", "Id") ?? "",
    configType: pickString(source, "configType", "ConfigType") ?? "",
    version: pickNumber(source, "version", "Version") ?? 0,
    name: pickString(source, "name", "Name") ?? "",
    application: pickString(source, "application", "Application") ?? "",
    created: pickString(source, "created", "Created") ?? "",
    lastUpdated: pickString(source, "lastUpdated", "LastUpdated") ?? "",
    createdBy: pickString(source, "createdBy", "CreatedBy") ?? "",
    lastUpdatedBy: pickString(source, "lastUpdatedBy", "LastUpdatedBy") ?? "",
    userApis: normalizeUserApis(source.userApis ?? source.UserApis),
    organisations: pickStringArray(source, "organisations", "Organisations"),
  };
};

const useClusters = () => {
  const currentToken = useUserQueryStore((s) => s.userQuery.currentToken);
  const apiClient = new APIClient<MultiTenantClusterGetDto>("/MultiTenantCluster", currentToken!);

  return useQuery({
    queryKey: ["clusters"],
    queryFn: async () => {
      const response = await apiClient.getAll();
      const clusters = Array.isArray(response) ? response : [];
      return clusters.map(normalizeCluster);
    },
    staleTime: ms("1m"),
  });
};

export default useClusters;
